/**
 * Sortable Massnahme Row Component
 * 
 * Displays a row in the shift planning table with drag-and-drop functionality.
 * Extracted from Shifts.jsx for modularity.
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ChevronRight,
  ChevronDown,
  Calendar,
  User,
  Trash2,
  AlertTriangle,
  DoorOpen,
  Users,
  GripVertical,
  Plus,
  Copy,
  Clock,
  Repeat,
  ArrowUp,
  ArrowDown,
  Coffee,
  Building2,
} from 'lucide-react';
import { format } from 'date-fns';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatDateDE, ACTIVITY_TYPES } from './index';

function SortableMassnahmeRow({ 
  massnahme, 
  weekDays, 
  getShiftsForMassnahmeAndDay, 
  getActivitiesForMassnahmeAndDay, 
  getEmployeeConflicts,
  getAbsenceForShift, 
  canManage, 
  openNewShiftDialog, 
  openEditShiftDialog, 
  openActivityDialog,
  openEditActivityDialog,
  handleDeleteActivity, 
  isCollapsed, 
  onToggleCollapse, 
  onCopyShift, 
  onShiftDrop,
  onActivityDrop,
  onReorderEntry,
  onReorderBeforeTarget,
  holidayMap = {},
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: massnahme.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 1,
  };
  
  // Handle drop on cell — Drop in eine Zelle dieser Massnahme.
  // Kommt der Einsatz/die Aktivität aus einer ANDEREN Maßnahme, wird sie
  // beim Verschieben auch der Ziel-Maßnahme zugeordnet (Drag&Drop zwischen Maßnahmen).
  const handleDrop = (e, day) => {
    e.preventDefault();
    const shiftId = e.dataTransfer.getData('shiftId');
    const activityId = e.dataTransfer.getData('activityId');
    const sourceMassnahmeId = e.dataTransfer.getData('sourceMassnahmeId');
    const newMassnahmeId = sourceMassnahmeId && sourceMassnahmeId !== massnahme.id
      ? massnahme.id
      : null;
    if (shiftId) {
      onShiftDrop(shiftId, format(day, 'yyyy-MM-dd'), newMassnahmeId);
    } else if (activityId && onActivityDrop) {
      onActivityDrop(activityId, format(day, 'yyyy-MM-dd'), newMassnahmeId);
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b hover:bg-muted/20">
      <td className="p-3">
        <div className="flex items-center gap-3 min-w-0">
          {/* Collapse button */}
          <button
            onClick={() => onToggleCollapse(massnahme.id)}
            className="p-1 rounded hover:bg-muted/50 text-muted-foreground flex-shrink-0"
          >
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {canManage && (
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted/50 text-muted-foreground flex-shrink-0"
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
            style={{ backgroundColor: massnahme.color }}
          >
            {massnahme.short_code.slice(0, 3)}
          </div>
          <div className="min-w-0">
            <p className="font-medium break-words leading-tight">{massnahme.name}</p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground mt-0.5">
              <span className="whitespace-nowrap">{massnahme.short_code}</span>
              {!massnahme.location_id && (
                <span
                  className="flex items-center gap-1 text-indigo-600 font-medium whitespace-nowrap"
                  title="Standortübergreifende Maßnahme"
                  data-testid={`massnahme-firmenweit-grid-${massnahme.id}`}
                >
                  <Building2 className="h-3 w-3 flex-shrink-0" />
                  Firmenweit
                </span>
              )}
              {massnahme.teilnehmer_anzahl && (
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <Users className="h-3 w-3 flex-shrink-0" />
                  {massnahme.teilnehmer_anzahl} TN
                </span>
              )}
              {(massnahme.start_time || massnahme.end_time) && (
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <Clock className="h-3 w-3 flex-shrink-0" />
                  {massnahme.start_time || '—'} - {massnahme.end_time || '—'}
                </span>
              )}
            </div>
            {(massnahme.start_date || massnahme.end_date) && (
              <div className="text-xs text-muted-foreground mt-1">
                {massnahme.start_date && massnahme.end_date 
                  ? `${formatDateDE(massnahme.start_date)} - ${formatDateDE(massnahme.end_date)}`
                  : massnahme.start_date 
                    ? `Ab ${formatDateDE(massnahme.start_date)}`
                    : `Bis ${formatDateDE(massnahme.end_date)}`
                }
              </div>
            )}
            {massnahme.excluded_weeks?.length > 0 && (
              <div className="text-xs text-amber-600 mt-1">
                Ausgeschlossen: {massnahme.excluded_weeks.slice(0, 3).join(', ')}
                {massnahme.excluded_weeks.length > 3 && ` +${massnahme.excluded_weeks.length - 3}`}
              </div>
            )}
            {massnahme.scheduled_weeks?.length > 0 && (
              <div className="text-xs text-primary mt-1">
                Nur in: {massnahme.scheduled_weeks.slice(0, 3).join(', ')}
                {massnahme.scheduled_weeks.length > 3 && ` +${massnahme.scheduled_weeks.length - 3}`}
              </div>
            )}
          </div>
        </div>
      </td>
      {weekDays.map(day => {
        const dayShifts = getShiftsForMassnahmeAndDay(massnahme.id, day);
        const dayActivities = getActivitiesForMassnahmeAndDay(massnahme.id, day);
        const dayKey = format(day, 'yyyy-MM-dd');
        const holiday = holidayMap[dayKey];
        
        // If collapsed, show summary
        if (isCollapsed) {
          const totalCount = dayShifts.length + dayActivities.length;
          return (
            <td key={day.toISOString()} className={`p-2 ${holiday ? 'bg-red-50/60 dark:bg-red-950/10' : ''}`}>
              <div className="min-h-[30px] rounded-lg p-2 flex items-center justify-center">
                {totalCount > 0 ? (
                  <Badge variant="secondary" className="text-xs">
                    {totalCount} {totalCount === 1 ? 'Eintrag' : 'Einträge'}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">-</span>
                )}
              </div>
            </td>
          );
        }
        
        return (
          <td key={day.toISOString()} className={`p-2 ${holiday ? 'bg-red-50/60 dark:bg-red-950/10' : ''}`}>
            <div 
              className={`min-h-[80px] rounded-lg p-2 space-y-1 border-2 border-dashed border-transparent transition-all ${
                canManage ? 'hover:border-primary/30 hover:bg-primary/5' : ''
              }`}
              onDrop={(e) => handleDrop(e, day)}
              onDragOver={handleDragOver}
            >
              {/* Feiertag-Badge */}
              {holiday && (
                <div
                  className="text-[10px] px-2 py-0.5 rounded-md bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800/50 font-medium truncate flex items-center gap-1"
                  title={`Feiertag: ${holiday.name}`}
                  data-testid={`holiday-badge-${dayKey}`}
                >
                  <span aria-hidden="true">★</span>
                  <span className="truncate">{holiday.name}</span>
                </div>
              )}
              {/* Kombinierte Reihenfolge fuer dieses Zelle: nach sort_order
                  sortiert (gleiche Logik wie in der TV-Ansicht). Items ohne
                  sort_order landen ans Ende, Tie-break: Aktivitaeten vor
                  Shifts, dann nach created_at. Reorder-Buttons aendern die
                  Position innerhalb dieser zusammengefuehrten Liste. */}
              {(() => {
                const cellItems = [
                  ...dayActivities.map(a => ({ ...a, _type: 'activity' })),
                  ...dayShifts.map(s => ({ ...s, _type: 'shift' })),
                ].sort((a, b) => {
                  const ao = a.sort_order ?? Number.MAX_SAFE_INTEGER;
                  const bo = b.sort_order ?? Number.MAX_SAFE_INTEGER;
                  if (ao !== bo) return ao - bo;
                  if (a._type !== b._type) return a._type === 'activity' ? -1 : 1;
                  return (a.created_at || '').localeCompare(b.created_at || '');
                });
                const totalCount = cellItems.length;
                const dayKeyStr = format(day, 'yyyy-MM-dd');
                // Wird auf einer Item-Card aufgerufen und reordert ggf. innerhalb
                // der Zelle. Stoppt Bubbling, damit die Cell-Background-Logik
                // (cross-cell move) nicht zusaetzlich ausgeloest wird.
                const handleItemDrop = (e, targetItem) => {
                  if (!canManage || !onReorderBeforeTarget) return;
                  const sourceMassnahmeId = e.dataTransfer.getData('sourceMassnahmeId');
                  const sourceDate = e.dataTransfer.getData('sourceDate');
                  // Nur Same-Cell-Reorder: wenn die Quelle aus dieser Massnahme
                  // und diesem Tag stammt. Cross-Cell laueft weiter ueber den
                  // Cell-Background-Drop-Handler.
                  if (sourceMassnahmeId !== massnahme.id || sourceDate !== dayKeyStr) return;
                  const shiftId = e.dataTransfer.getData('shiftId');
                  const activityId = e.dataTransfer.getData('activityId');
                  const draggedId = shiftId || activityId;
                  if (!draggedId || draggedId === targetItem.id) return;
                  const dragged = cellItems.find((it) => it.id === draggedId);
                  if (!dragged) return;
                  e.preventDefault();
                  e.stopPropagation();
                  onReorderBeforeTarget(dragged, targetItem, cellItems);
                };
                return cellItems.map((item, idx) => {
                  const canMoveUp = canManage && onReorderEntry && idx > 0;
                  const canMoveDown = canManage && onReorderEntry && idx < totalCount - 1;
                  if (item._type === 'activity') {
                    const activity = item;
                    const actType = ACTIVITY_TYPES.find(t => t.value === activity.activity_type);
                    return (
                      <div
                        key={activity.id}
                        draggable={canManage}
                        onDragStart={(e) => {
                          e.dataTransfer.setData('activityId', activity.id);
                          e.dataTransfer.setData('sourceMassnahmeId', massnahme.id);
                          e.dataTransfer.setData('sourceDate', dayKeyStr);
                        }}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={(e) => handleItemDrop(e, activity)}
                        onClick={(e) => { e.stopPropagation(); if (canManage && openEditActivityDialog) openEditActivityDialog(activity); }}
                        className={`text-xs p-2 rounded-lg bg-gradient-to-r from-gray-100 to-gray-50 border border-gray-200 group relative transition-all hover:scale-[1.02] ${
                          canManage ? 'cursor-pointer' : ''
                        }`}
                        title={canManage ? 'Klicken zum Bearbeiten' : ''}
                        data-testid={`activity-card-${activity.id}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <div 
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: actType?.color || '#6B7280' }}
                          />
                          <span className="font-medium truncate flex-1">{activity.activity_label}</span>
                          {canManage && (canMoveUp || canMoveDown) && (
                            <span className="flex items-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all">
                              {canMoveUp && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); onReorderEntry(activity.id, cellItems, -1); }}
                                  className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"
                                  title="Nach oben verschieben"
                                  data-testid={`reorder-up-${activity.id}`}
                                >
                                  <ArrowUp className="h-3 w-3" />
                                </button>
                              )}
                              {canMoveDown && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); onReorderEntry(activity.id, cellItems, 1); }}
                                  className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"
                                  title="Nach unten verschieben"
                                  data-testid={`reorder-down-${activity.id}`}
                                >
                                  <ArrowDown className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          )}
                          {canManage && onCopyShift && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onCopyShift(activity, { type: 'activity' }); }}
                              className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-primary transition-all"
                              title="Aktivität kopieren"
                              data-testid={`copy-activity-btn-${activity.id}`}
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          )}
                          {canManage && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteActivity(activity.id); }}
                              className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-red-500 hover:text-red-700"
                              title="Aktivität löschen"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                        {(activity.start_time || activity.end_time) && (
                          <div className="text-muted-foreground mt-0.5 text-[10px]">
                            {activity.start_time || '–'}{activity.end_time ? ` – ${activity.end_time}` : ''}
                          </div>
                        )}
                        {activity.participant_count && (
                          <div className="text-muted-foreground mt-1">
                            {activity.participant_count} Teilnehmer
                          </div>
                        )}
                      </div>
                    );
                  }
                  // Shift
                  const shift = item;
                  const conflicts = getEmployeeConflicts(shift, day);
                  const hasConflict = conflicts.length > 1;
                  const absence = getAbsenceForShift ? getAbsenceForShift(shift.employee_id, dayKeyStr) : null;
                  // Markierungen priorisieren: genehmigte Abwesenheit > beantragter Urlaub > Doppelbuchung.
                  // Pro Element wird genau ein optischer Indikator (Ring/Outline) gesetzt.
                  let statusClass = '';
                  if (absence && absence.status === 'approved') {
                    statusClass = 'ring-2 ring-red-500 ring-offset-1';
                  } else if (absence && absence.status === 'pending') {
                    statusClass = 'outline outline-2 outline-offset-1 outline-dashed outline-red-400';
                  } else if (hasConflict) {
                    statusClass = 'ring-2 ring-amber-400 ring-offset-1';
                  }
                  return (
                    <div
                      key={shift.id}
                      draggable={canManage}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('shiftId', shift.id);
                        e.dataTransfer.setData('sourceMassnahmeId', massnahme.id);
                        e.dataTransfer.setData('sourceDate', dayKeyStr);
                      }}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={(e) => handleItemDrop(e, shift)}
                      onClick={(e) => { e.stopPropagation(); if (canManage) openEditShiftDialog(shift); }}
                      title={absence ? absence.tooltip : undefined}
                      data-testid={absence ? `shift-absence-${absence.status}-${shift.id}` : undefined}
                      className={`text-xs p-2 rounded-lg cursor-pointer transition-all hover:scale-[1.02] bg-card shadow-sm border group ${statusClass} ${canManage ? 'cursor-grab active:cursor-grabbing' : ''}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <div 
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium text-white shrink-0"
                          style={{ backgroundColor: massnahme.color }}
                        >
                          {shift.employee_name?.split(' ').reverse().map(n => n[0]).join('')}
                        </div>
                        <span className="font-medium truncate flex-1">{shift.employee_name}</span>
                        {shift.series_id && (
                          <Repeat
                            className="h-3 w-3 text-primary/70 flex-shrink-0"
                            title="Teil einer Serie"
                            data-testid={`series-marker-${shift.id}`}
                          />
                        )}
                        {hasConflict && (
                          <AlertTriangle className="h-3 w-3 text-amber-500 flex-shrink-0" />
                        )}
                        {canManage && (canMoveUp || canMoveDown) && (
                          <span className="flex items-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all">
                            {canMoveUp && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onReorderEntry(shift.id, cellItems, -1); }}
                                className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"
                                title="Nach oben verschieben"
                                data-testid={`reorder-up-${shift.id}`}
                              >
                                <ArrowUp className="h-3 w-3" />
                              </button>
                            )}
                            {canMoveDown && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onReorderEntry(shift.id, cellItems, 1); }}
                                className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-primary"
                                title="Nach unten verschieben"
                                data-testid={`reorder-down-${shift.id}`}
                              >
                                <ArrowDown className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        )}
                        {canManage && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onCopyShift(shift); }}
                            className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-primary transition-all"
                            title="Einsatz kopieren"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span>{shift.start_time && shift.end_time ? `${(shift.start_time || '').slice(0, 5)}\u2013${(shift.end_time || '').slice(0, 5)}` : 'Ganztags'}</span>
                        {(() => {
                          const dmap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
                          const wd = shift.date ? dmap[new Date(`${shift.date}T00:00:00`).getDay()] : null;
                          const wp = massnahme.weekday_pauses;
                          const p = (wp && typeof wp === 'object' && wd && wp[wd] != null && wp[wd] !== '')
                            ? Number(wp[wd])
                            : (massnahme.pause_minutes != null && massnahme.pause_minutes !== '' ? Number(massnahme.pause_minutes) : 0);
                          return p > 0 ? (
                            <span className="flex items-center gap-0.5" title={`${p} Min Pause`}>
                              <Coffee className="h-2.5 w-2.5" />
                              {p} Min
                            </span>
                          ) : null;
                        })()}
                        {shift.room_name && (
                          <span className="flex items-center gap-0.5">
                            <DoorOpen className="h-2.5 w-2.5" />
                            {shift.room_name}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}

              {/* Action Buttons für leere Zellen */}
              {dayShifts.length === 0 && dayActivities.length === 0 && canManage && (
                <div className="flex items-center justify-center h-full gap-1">
                  <button
                    onClick={() => openNewShiftDialog(day, massnahme.id)}
                    className="p-1.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                    title="Neuer Einsatz"
                  >
                    <User className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openActivityDialog(day, massnahme.id)}
                    className="p-1.5 rounded hover:bg-blue-100 text-muted-foreground hover:text-blue-600 transition-colors"
                    title="Neue Aktivität (Berufsschule, etc.)"
                  >
                    <Calendar className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Add Buttons wenn schon Einträge existieren */}
              {(dayShifts.length > 0 || dayActivities.length > 0) && canManage && (
                <div className="flex items-center justify-center gap-1 pt-1 border-t border-dashed border-muted/50 mt-1">
                  <button
                    onClick={() => openNewShiftDialog(day, massnahme.id)}
                    className="p-1 rounded hover:bg-primary/10 text-muted-foreground/50 hover:text-primary transition-colors"
                    title="Weiterer Einsatz"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => openActivityDialog(day, massnahme.id)}
                    className="p-1 rounded hover:bg-blue-100 text-muted-foreground/50 hover:text-blue-600 transition-colors"
                    title="Weitere Aktivität"
                  >
                    <Calendar className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          </td>
        );
      })}
    </tr>
  );
}

export default SortableMassnahmeRow;
